<template>
    <themed-modal
        name="comment-edit"
        title="Edit Comment"
        size="lg"
        @closed="handleClosed"
    >
        <themed-form
            :fields="formFields"
            :model-value="formData"
            :errors="formErrors"
            layout="vertical"
            @input="formData = $event"
        />

        <div v-if="comment.attachment_id && comment.attachment" class="edit-comment__attachment">
            <div class="edit-comment__attachment-preview">
                <div v-if="comment.attachment.is_image" class="edit-comment__image-preview">
                    <a :href="`#${comment.attachment.name}`">
                        <img :src="comment.attachment.view_url" class="edit-comment__thumbnail" />
                    </a>
                    <a href="#_" class="lightbox" :id="`${comment.attachment.name}`">
                        <img :src="comment.attachment.view_url" />
                    </a>
                </div>
                <div v-else v-html="comment.attachment.icon" class="edit-comment__file-icon"></div>

                <div class="edit-comment__attachment-info">
                    <div class="edit-comment__attachment-details">
                        <a 
                            v-if="comment.attachment.is_image"
                            :href="`#${comment.attachment.name}`"
                            class="edit-comment__attachment-name"
                        >
                            {{ comment.attachment.name | truncate(45) }}
                        </a>
                        <p v-else class="edit-comment__attachment-name">
                            {{ comment.attachment.name | truncate(45) }}
                        </p>
                        <p v-if="comment.attachment.description" class="edit-comment__attachment-description">
                            {{ comment.attachment.description }}
                        </p>
                    </div>
                    <div class="edit-comment__attachment-actions">
                        <themed-button
                            variant="secondary"
                            size="sm"
                            :href="comment.attachment.view_url"
                            target="_blank"
                        >
                            View
                        </themed-button>
                        <themed-button
                            variant="secondary"
                            size="sm"
                            :href="comment.attachment.download_url"
                        >
                            Download
                        </themed-button>
                        <themed-button
                            variant="destructive"
                            size="sm"
                            @click="removeAttachment"
                        >
                            Remove
                        </themed-button>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="edit-comment__attachment-section">
            <h4 class="edit-comment__attachment-title">Add Attachment</h4>
            
            <div class="edit-comment__attachment-selects">
                <themed-form-field label="Category">
                    <themed-select
                        v-model="selectedCategory"
                        :options="categoryOptions"
                        @input="handleCategoryChange"
                    />
                </themed-form-field>

                <themed-form-field v-if="selectedCategory" label="Subcategory">
                    <themed-select
                        v-model="selectedSubcategory"
                        :options="subcategoryOptions"
                        @input="handleSubcategoryChange"
                    />
                </themed-form-field>
            </div>

            <themed-form-field v-if="availableDocuments.length" label="Document">
                <themed-select
                    v-model="comment.attachment_id"
                    :options="documentOptions"
                />
            </themed-form-field>

            <div v-if="selectedCategory && !availableDocuments.length" class="edit-comment__no-documents">
                No Documents Found
            </div>
        </div>

        <template #footer>
            <div class="modal-actions">
                <themed-button variant="secondary" @click="hideModal">
                    Cancel
                </themed-button>
                <themed-button 
                    variant="default" 
                    @click="save"
                    :disabled="saving"
                >
                    {{ saving ? 'Saving...' : 'Save Comment' }}
                </themed-button>
            </div>
        </template>
    </themed-modal>
</template>

<script>
import { mapState } from 'vuex';
import ThemedModal from '../ThemedModal.vue';
import ThemedForm from '../form/ThemedForm.vue';
import ThemedFormField from '../form/ThemedFormField.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemedButton from '../ThemedButton.vue';

export default {
    name: 'EditCommentModal',
    
    components: {
        ThemedModal,
        ThemedForm,
        ThemedFormField,
        ThemedSelect,
        ThemedButton
    },

    props: {
        commentId: {
            type: Number,
            required: true
        }
    },

    data() {
        return {
            comment: null,
            formData: {},
            formErrors: {},
            saving: false,
            selectedCategory: '',
            selectedSubcategory: ''
        };
    },

    computed: {
        ...mapState(['comments', 'documents']),

        formFields() {
            return [
                {
                    name: 'comment',
                    type: 'textarea',
                    label: 'Comment',
                    placeholder: 'Enter your comment here',
                    rows: 10,
                    required: true
                }
            ];
        },

        categoryOptions() {
            return [
                { value: '', label: 'Select Category' },
                ...this.documents.categories.map(cat => ({
                    value: cat.id,
                    label: cat.name
                }))
            ];
        },

        subcategoryOptions() {
            const subcategories = this.documents.subcategories.filter(
                sub => sub.category_id == this.selectedCategory
            );
            return [
                { value: '', label: 'Select Subcategory' },
                ...subcategories.map(sub => ({
                    value: sub.id,
                    label: sub.name
                }))
            ];
        },

        availableDocuments() {
            if (!this.comment || !this.comment.commentable || !this.comment.commentable.documents) {
                return [];
            }
            
            return this.comment.commentable.documents.filter(file => {
                if (this.selectedSubcategory) {
                    return file.category_id == this.selectedCategory && 
                           file.subcategory_id == this.selectedSubcategory;
                } else if (this.selectedCategory) {
                    return file.category_id == this.selectedCategory;
                }
                return false;
            });
        },

        documentOptions() {
            return [
                { value: '', label: 'Select Document' },
                ...this.availableDocuments.map(doc => ({
                    value: doc.id,
                    label: doc.name
                }))
            ];
        }
    },

    created() {
        this.comment = _.clone(_.find(this.comments.all, comment => comment.id == this.commentId));
        if (this.comment) {
            this.formData = {
                comment: this.comment.comment
            };
        }
    },

    methods: {
        hideModal() {
            this.$modal.hide('comment-edit');
        },

        handleClosed() {
            this.formData = {};
            this.formErrors = {};
            this.saving = false;
        },

        removeAttachment() {
            this.comment.attachment_id = null;
        },

        handleCategoryChange() {
            this.selectedSubcategory = '';
            this.comment.attachment_id = '';
        },

        handleSubcategoryChange() {
            this.comment.attachment_id = '';
        },

        async save() {

            this.saving = true;
            this.formErrors = {};

            // Update comment object with form data
            this.comment.comment = this.formData.comment;

            axios.patch('/comments/' + this.comment.id, this.comment)
                .then(response => {
                    this.comment = response.data.comment;
                    this.$emit('comment-updated', response.data.comment);

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: response.data.message
                    });

                    this.hideModal();
                })
                .catch(error => {
                    this.formErrors = error.response?.data?.errors || {};
                    
                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: error.response?.data?.message || 'Failed to save comment'
                    });
                })
                .finally(() => {
                    this.saving = false;
                });
        }
    }
};
</script>
